import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";

import {
  intakeService,
  INTAKE_FIELDS,
  parseHeaders,
} from "../services/intakeService";

// Google Form intake setup for one job.
//
// Two things have to be true before applications can arrive:
//   1. a column mapping (Google Form headers are free text, so this is per job)
//   2. an installed Apps Script holding this job's token
//
// The script runs as the Form owner, so the tenant's Sheet and Drive stay
// private and no Google credential is shared with us.

const fmtWhen = (iso) => {
  if (!iso) return "never";
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
};

const IntakeSetupPanel = ({ jobId, onImported }) => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  const [headerText, setHeaderText] = useState("");
  const [headers, setHeaders] = useState([]);
  const [map, setMap] = useState({});
  const [showMapping, setShowMapping] = useState(false);

  const [scriptDialog, setScriptDialog] = useState(null);
  const [showFailures, setShowFailures] = useState(false);

  const notify = (msg, sev = "success") => setSnack({ open: true, msg, sev });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await intakeService.status(jobId);
      const d = res?.data || {};
      setStatus(d);
      if (d.column_map) {
        setMap(d.column_map);
        // Existing mapping implies the headers, so the paste step can be skipped.
        setHeaders(Object.values(d.column_map).filter(Boolean));
      }
      setShowMapping(!d.column_map);
    } catch (e) {
      setError(
        e?.response?.data?.statusMessage || "Could not load intake status",
      );
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleParseHeaders = async () => {
    const parsed = parseHeaders(headerText);
    if (!parsed.length) {
      notify("Paste the header row from your response sheet first", "warning");
      return;
    }
    setHeaders(parsed);
    setBusy(true);
    try {
      const { data: res } = await intakeService.suggestMap(jobId, parsed);
      const suggestion = res?.data?.suggestion || {};
      setMap((prev) => ({ ...suggestion, ...prev }));
      const hit = Object.keys(suggestion).length;
      notify(
        hit
          ? `Matched ${hit} of ${parsed.length} columns — check them below`
          : "No columns matched automatically; map them manually below",
        hit ? "success" : "info",
      );
    } catch {
      notify("Could not suggest a mapping; map the columns manually", "info");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveMap = async () => {
    if (!map.consent) {
      notify(
        "A consent question must be mapped — applications cannot be imported without one",
        "error",
      );
      return;
    }
    if (!map.email && !map.phone) {
      notify("Map either email or phone so candidates can be identified", "error");
      return;
    }
    setBusy(true);
    try {
      const clean = {};
      for (const [k, v] of Object.entries(map)) if (v) clean[k] = v;
      await intakeService.saveMap(jobId, clean);
      notify("Column mapping saved");
      await load();
    } catch (e) {
      notify(e?.response?.data?.statusMessage || "Could not save mapping", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleRotate = async () => {
    setBusy(true);
    try {
      const { data: res } = await intakeService.rotateToken(jobId);
      setScriptDialog(res?.data || null);
      await load();
    } catch (e) {
      notify(e?.response?.data?.statusMessage || "Could not issue token", "error");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text, what = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      notify(what);
    } catch {
      notify("Copy failed — select the text and copy manually", "warning");
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, borderRadius: 3, mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  const mappingDone = Boolean(status?.configured);
  const tokenDone = Boolean(status?.token_issued);
  const receiving = Boolean(status?.last_received_at);
  const activeStep = !mappingDone ? 0 : !tokenDone ? 1 : !receiving ? 2 : 3;

  return (
    <Paper sx={{ p: 3, borderRadius: 3, mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
            Application intake
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Applications are pushed from your own Google Form. Your sheet and
            uploaded files stay private.
          </Typography>
        </Box>
        <Tooltip title="Refresh status">
          <IconButton onClick={load} disabled={busy}>
            <RefreshRoundedIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        <Step completed={mappingDone}>
          <StepLabel>Map your columns</StepLabel>
        </Step>
        <Step completed={tokenDone}>
          <StepLabel>Install the script</StepLabel>
        </Step>
        <Step completed={receiving}>
          <StepLabel>Receive a test response</StepLabel>
        </Step>
      </Stepper>

      {/* Staleness is the only signal that a trigger has broken — nothing errors
          on our side when it stops firing. */}
      {status?.is_stale && (
        <Alert
          severity="warning"
          icon={<WarningAmberRoundedIcon />}
          sx={{ mb: 2, borderRadius: 2 }}
        >
          <AlertTitle sx={{ fontWeight: 700 }}>
            No applications received recently
          </AlertTitle>
          Nothing has arrived {status.last_received_at ? `since ${fmtWhen(status.last_received_at)}` : "yet"}.
          If your form is live, the trigger may have been removed or hit a quota.
          Open the script and run <code>backfill()</code> to replay missed
          responses — it is safe to run more than once.
        </Alert>
      )}

      {receiving && !status?.is_stale && (
        <Alert
          severity="success"
          icon={<CheckCircleRoundedIcon />}
          sx={{ mb: 2, borderRadius: 2 }}
        >
          Receiving applications. Last one {fmtWhen(status.last_received_at)}.
        </Alert>
      )}

      {/* Counters */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: "Received", value: status?.rows_total ?? 0 },
          { label: "Rejected", value: status?.rows_failed ?? 0 },
          { label: "Last response", value: fmtWhen(status?.last_received_at) },
        ].map((s) => (
          <Grid item xs={4} key={s.label}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: "action.hover",
                textAlign: "center",
              }}
            >
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                {s.value}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                {s.label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* ── Step 1: mapping ─────────────────────────────────────────────── */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
          }}
          onClick={() => setShowMapping((v) => !v)}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            Column mapping
          </Typography>
          {mappingDone && (
            <Chip size="small" color="success" label="Saved" variant="outlined" />
          )}
          <ExpandMoreRoundedIcon
            sx={{
              ml: "auto",
              transform: showMapping ? "rotate(180deg)" : "none",
              transition: "transform .2s",
            }}
          />
        </Box>

        <Collapse in={showMapping}>
          <Box sx={{ pt: 2 }}>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1.5 }}>
              Google Form question titles are free text, so we need to know which
              question means what. Open your form's response sheet, copy the
              entire first row, and paste it here.
            </Typography>

            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              placeholder="Timestamp	Full Name	Email Address	WhatsApp Number	Upload your CV	I agree to the privacy notice"
              value={headerText}
              onChange={(e) => setHeaderText(e.target.value)}
              sx={{ mb: 1.5 }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<AutoFixHighRoundedIcon />}
              onClick={handleParseHeaders}
              disabled={busy || !headerText.trim()}
              sx={{ mb: 2 }}
            >
              Detect columns
            </Button>

            {headers.length > 0 && (
              <Grid container spacing={2}>
                {INTAKE_FIELDS.map((f) => (
                  <Grid item xs={12} md={6} key={f.key}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label={`${f.label}${f.required ? " *" : ""}`}
                      helperText={f.help}
                      value={map[f.key] || ""}
                      onChange={(e) =>
                        setMap((p) => ({ ...p, [f.key]: e.target.value }))
                      }
                    >
                      <MenuItem value="">
                        <em>Not collected</em>
                      </MenuItem>
                      {headers.map((h) => (
                        <MenuItem key={h} value={h}>
                          {h}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleSaveMap}
                    disabled={busy}
                  >
                    Save mapping
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>
        </Collapse>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* ── Step 2: script ──────────────────────────────────────────────── */}
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
          Form script
        </Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1.5 }}>
          {tokenDone
            ? `A script is installed for this job (issued ${fmtWhen(status.token_rotated_at)}). Re-issuing invalidates the old one immediately — you will need to paste the new script into your form.`
            : "Generate the script to paste into your Google Form. It runs as you, so nothing is shared with us."}
        </Typography>
        <Button
          variant={tokenDone ? "outlined" : "contained"}
          onClick={handleRotate}
          disabled={busy || !mappingDone}
        >
          {tokenDone ? "Re-issue script" : "Generate script"}
        </Button>
        {!mappingDone && (
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1 }}>
            Save the column mapping first — the script needs to know which
            question holds the resume.
          </Typography>
        )}
      </Box>

      {/* Rejected rows: almost always a missing consent answer or a mapping
          mistake, so the reason matters more than the count. */}
      {status?.recent_failures?.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => setShowFailures((v) => !v)}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              Rejected responses ({status.recent_failures.length})
            </Typography>
            <ExpandMoreRoundedIcon
              sx={{
                ml: "auto",
                transform: showFailures ? "rotate(180deg)" : "none",
                transition: "transform .2s",
              }}
            />
          </Box>
          <Collapse in={showFailures}>
            <Box sx={{ pt: 1.5 }}>
              {status.recent_failures.map((f) => (
                <Box
                  key={f.row_hash}
                  sx={{
                    p: 1.25,
                    mb: 1,
                    borderRadius: 2,
                    bgcolor: "error.lighter",
                    border: "1px solid",
                    borderColor: "error.light",
                  }}
                >
                  <Typography sx={{ fontSize: 13 }}>{f.error}</Typography>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    {fmtWhen(f.received_at)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </>
      )}

      {/* ── Script dialog: the token is shown exactly once ───────────────── */}
      <Dialog
        open={Boolean(scriptDialog)}
        onClose={() => setScriptDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Paste this into your Google Form
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this now. The token inside it is shown once and cannot be
            retrieved later — if you lose it you will have to re-issue, which
            invalidates the script you installed.
          </Alert>

          {scriptDialog?.setup_steps && (
            <Box sx={{ mb: 2 }}>
              {scriptDialog.setup_steps.map((s, i) => (
                <Typography key={i} sx={{ fontSize: 13, mb: 0.5 }}>
                  <strong>{i + 1}.</strong> {s}
                </Typography>
              ))}
            </Box>
          )}

          <Box
            component="pre"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "grey.900",
              color: "grey.100",
              fontSize: 11,
              lineHeight: 1.5,
              maxHeight: 340,
              overflow: "auto",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
          >
            {scriptDialog?.script}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setScriptDialog(null)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<ContentCopyRoundedIcon />}
            onClick={() => copy(scriptDialog?.script, "Script copied")}
          >
            Copy script
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.sev} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default IntakeSetupPanel;
